import { useState } from "react";
import { Clock, Download, FileText, Loader2, MoreHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { ActionMenu } from "@/components/ui/action-menu";
import { getDownloadUrl } from "@/services/document/documentVersionService";
import { DocumentVersionInfo } from "@/services/document/documentTypes";
import { toast } from "sonner";
import { formatBytes } from "@/lib/format";

interface DocumentVersionsListProps {
  versions: DocumentVersionInfo[];
  isLoading: boolean;
  selectedVersionId: string | null;
  onVersionSelect: (versionId: string) => void;
  onPromoteVersion?: (versionId: string) => void;
  onDeleteVersion?: (versionId: string) => void;
}

export function DocumentVersionsList({
  versions,
  isLoading,
  selectedVersionId,
  onVersionSelect,
  onPromoteVersion,
  onDeleteVersion
}: DocumentVersionsListProps) {
  const [hoveredVersionId, setHoveredVersionId] = useState<string | null>(null);

  const currentVersionId = selectedVersionId || (versions && versions.length > 0 ? versions[0].id : null);
  
  // Download a specific version
  const handleDownloadVersion = async (version: DocumentVersionInfo) => {
    try {
      const url = await getDownloadUrl(version.file_path);
      
      // Create a hidden link and simulate a click to download
      const link = window.document.createElement('a');
      link.href = url;
      link.download = `Document-v${version.version}`;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      
      toast.success(`Downloading version ${version.version}`);
    } catch (error) {
      toast.error("Failed to generate download link");
    }
  };

  // Get action items for each version
  const getVersionActions = (version: DocumentVersionInfo, isLatest: boolean) => {
    const actions = [
      {
        label: "Download",
        icon: <Download className="mr-2 h-4 w-4" />,
        onClick: () => handleDownloadVersion(version),
      }
    ];

    // Only allow promotion of non-latest versions to become the latest
    if (!isLatest && onPromoteVersion) {
      actions.push({
        label: "Promote to latest",
        icon: <FileText className="mr-2 h-4 w-4" />,
        onClick: () => onPromoteVersion(version.id),
      });
    }

    // Only allow deletion of non-latest versions
    if (!isLatest && onDeleteVersion) {
      actions.push({
        label: "Delete version",
        icon: <MoreHorizontal className="mr-2 h-4 w-4" />,
        variant: "destructive" as const,
        onClick: () => onDeleteVersion(version.id),
      });
    }

    return actions;
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Version History</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex justify-center py-4">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!versions || versions.length === 0) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Version History</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground py-2">No version history available</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg">Version History</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3 max-h-[400px] overflow-y-auto pr-1">
          {versions.map((version, index) => {
            const isLatest = index === 0;
            const isSelected = version.id === currentVersionId;
            const isHovered = version.id === hoveredVersionId;
            
            return (
              <div
                key={version.id}
                className={`relative flex items-start w-full text-left p-3 rounded-md hover:bg-muted transition-colors
                  ${isSelected ? 'bg-muted border border-muted-foreground/20' : ''}`}
                onMouseEnter={() => setHoveredVersionId(version.id)}
                onMouseLeave={() => setHoveredVersionId(null)}
              >
                <Button
                  variant="ghost"
                  className="absolute top-2 right-2 h-8 w-8 p-0"
                  asChild
                >
                  <div className={`${isHovered ? 'opacity-100' : 'opacity-0'} transition-opacity`}>
                    <ActionMenu
                      items={getVersionActions(version, isLatest)}
                      align="end"
                    />
                  </div>
                </Button>
                
                <button
                  className="flex items-start flex-1 text-left"
                  onClick={() => onVersionSelect(version.id)}
                >
                  <div className="h-10 w-10 rounded-full bg-muted-foreground/10 flex items-center justify-center flex-shrink-0 mr-3">
                    <FileText className="h-5 w-5 text-muted-foreground" />
                  </div>
                  
                  <div className="flex-1 mr-8">
                    <div className="flex items-center space-x-2">
                      <p className="text-sm font-medium">Version {version.version}</p>
                      {isLatest && (
                        <span className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                          Latest
                        </span>
                      )}
                    </div>
                    
                    <div className="flex items-center text-xs text-muted-foreground mt-1">
                      <Clock className="h-3 w-3 mr-1" />
                      {new Date(version.created_at).toLocaleString()}
                    </div>
                    
                    <div className="flex items-center mt-1 space-x-4">
                      <span className="text-xs text-muted-foreground">
                        {formatBytes(version.file_size)}
                      </span>
                      
                      {version.uploaded_by && (
                        <div className="flex items-center space-x-1">
                          <Avatar className="h-4 w-4">
                            <AvatarFallback className="text-[8px]">
                              US
                            </AvatarFallback>
                          </Avatar>
                          <span className="text-xs text-muted-foreground">
                            Updated by User
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}